import { useCallback, useEffect, useState } from "react";
import { UseFormSetValue } from "react-hook-form";

import { CreateCompanyEnrich } from "../schema/companyEnrich";
import { hasSameCountries } from "../utils/searchDefaultsForm";

type FormValues = Partial<CreateCompanyEnrich>;

/**
 * "Same as company location" under the person location (PREC-3666). Checked by default; while
 * checked, the person location mirrors the company location, so setting where the companies are
 * also sets where the people are — the filter Berg & Macher forgot. Unchecking leaves the person
 * location as it is, to edit on its own.
 *
 * A form that opens with values (a saved source, a prefill) starts checked only when the two
 * already agree: a person location that differs was chosen, and checking would overwrite it.
 */
export const useSameAsCompanyLocation = (
	formValues: FormValues,
	setValue: UseFormSetValue<CreateCompanyEnrich>
) => {
	const [mirrored, setMirrored] = useState(true);
	const companyCountries = formValues.companyCountries ?? [];
	const companyCountriesKey = companyCountries.join(",");

	const mirrorCompanyLocation = useCallback(
		(countries: string[]) =>
			setValue("countries", [...countries], { shouldDirty: true, shouldValidate: true }),
		[setValue]
	);

	useEffect(() => {
		if (mirrored && !hasSameCountries(formValues.countries, companyCountries)) {
			mirrorCompanyLocation(companyCountries);
		}
	}, [mirrored, companyCountriesKey]);

	/** The checkbox: checking copies the company location over the person location. */
	const setSameAsCompanyLocation = (checked: boolean) => {
		setMirrored(checked);

		if (checked) {
			mirrorCompanyLocation(companyCountries);
		}
	};

	/** Re-reads the state from values the form was just given. */
	const syncSameAsCompanyLocation = useCallback((values: FormValues) => {
		setMirrored(hasSameCountries(values.countries, values.companyCountries));
	}, []);

	return {
		sameAsCompanyLocation: mirrored,
		setSameAsCompanyLocation,
		syncSameAsCompanyLocation,
	};
};
